// The leader's Overview: who needs them, how each line is going at End-of-Line
// QC, the day hour by hour, and who has worked a pod.
//
// Ranked verbs first. A leader reading this walking wants the three things to
// do before the numbers, and each card carries its own button — go there,
// acknowledge, go to the feeder, close — so the list is a to-do list rather
// than a report.

import { useMemo, useState } from 'react';
import { AvatarTile, Skeleton } from '@/components/ui/misc';
import { FlowlineRow, ScoreRow, lineTone } from '@/components/home/Score';
import { NeedCardList } from '@/components/home/NeedCard';
import { TakeSheet, takeTargetOf } from '@/components/andon/TakeSheet';
import { paceWord } from '@/components/shell/AppHeader';
import { employeeAvatarUrl } from '@/api/client';
import type { AndonVM } from '@/api/types';
import { useT } from '@/i18n/useT';
import { paceLabel } from '@/lib/pace';
import { useCrewToday } from '@/queries';
import { useEolHourLines, useFloorLines, useFloorNeeds, useFloorScore } from '@/queries/leader';
import { SectionHead, paceSentence } from '@/screens/phone/HomeScreen';
import { useStore } from '@/store';

export function FloorOverview({
  tablet,
  flowlineIds,
  onDrillTo,
  onOpenAlerts,
  onOpenCrew,
  onPickFlowline,
}: {
  tablet: boolean;
  flowlineIds: number[];
  onDrillTo: (workcentreId: number, name: string) => void;
  onOpenAlerts: () => void;
  onOpenCrew: () => void;
  onPickFlowline: (flowlineId: number) => void;
}) {
  const t = useT();
  const openAndon = useStore((s) => s.openAndon);
  const openQuality = useStore((s) => s.openQualityAlert);
  const connMode = useStore((s) => s.connMode);
  const score = useFloorScore();
  const { lines, isPending } = useFloorLines(flowlineIds);
  // "Take it" on a Needs-you card opens the ETA sheet right here (frame v2.12
  // script L853), for a session that can sign the take — a PIN session is the
  // service account and names nobody, so its cards open the thread instead.
  const [taking, setTaking] = useState<AndonVM | null>(null);
  const canTake = connMode === 'user';
  const actions = useMemo(
    () => ({ goToStation: onDrillTo, openAndon, openQuality, ...(canTake ? { takeAndon: (a: AndonVM) => setTaking(a) } : {}) }),
    [onDrillTo, openAndon, openQuality, canTake],
  );
  const needs = useFloorNeeds(flowlineIds, actions);
  const hours = useEolHourLines(flowlineIds);
  const crew = useCrewToday();
  const onShift = (crew.data ?? []).filter((c) => c.open);

  const s = score.data;
  return (
    <>
      <section aria-label={t.needsYouT}>
        <SectionHead
          label={`${t.needsYouT} · ${needs.items.length}`}
          sub={t.worstFirstSubT}
          link={{ label: `${t.allLinkT} ›`, onClick: onOpenAlerts }}
        />
        <NeedCardList items={needs.items.slice(0, 4)} isPending={needs.isPending} />
        <TakeSheet target={taking ? takeTargetOf(taking) : null} onClose={() => setTaking(null)} onOpenThread={openAndon} />
      </section>

      <section aria-label={t.flowlinesEolTodayT}>
        <SectionHead label={t.flowlinesEolTodayT} />
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: '6px 14px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* On a phone the floor's score is the top row of this card — the
              total of the rows under it, in the same place the operator home
              keeps theirs. A tablet has it in the header. */}
          {!tablet &&
            (s ? (
              <ScoreRow
                done={s.done}
                target={s.target}
                sentence={paceSentence(s.dueNow, s.target, s.done, t)}
                chip={paceWord(s, t)}
                chipTone={lineTone(s)}
              />
            ) : score.isPending ? (
              <Skeleton height={46} radius={8} />
            ) : null)}
          {isPending && lines.length === 0 ? (
            <Skeleton height={44} radius={8} />
          ) : (
            lines.map((l) => {
              const ex = l.exceptions.length;
              const exText = ex
                ? [
                    l.stopped ? `${l.stopped} ${t.stoppedCountT}` : null,
                    l.starved ? `${l.starved} ${t.starvedCountT}` : null,
                    l.behind ? `${l.behind} ${t.behindCountT}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : t.nothingWrongT;
              return (
                <FlowlineRow
                  key={l.flowlineId}
                  short={l.short}
                  done={l.done}
                  target={l.target}
                  dueNow={l.dueNow}
                  delta={l.delta}
                  chip={l.delta == null ? '—' : paceLabel(l.delta).replace('-', '−')}
                  chipTone={lineTone(l)}
                  exText={exText}
                  exTone={l.stopped ? 'danger' : ex ? 'warn' : 'neutral'}
                  onClick={() => onPickFlowline(l.flowlineId)}
                />
              );
            })
          )}
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 12 }}>
        {hours.map((d) => {
          const peak = Math.max(1, ...d.bars.map((b) => b.count));
          return (
            <div key={d.flowlineId} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <span style={{ font: "700 14px 'Space Grotesk',sans-serif" }}>{d.short}</span>
                <span
                  style={{
                    font: '500 11.5px Manrope,sans-serif',
                    color: 'var(--sub)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {d.done} {t.routeOfDone} {d.target ?? '—'} {t.throughEolQcT}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 70 }}>
                {d.bars.map((h) => (
                  <span
                    key={h.hour}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}
                  >
                    <span
                      aria-label={`${hourLabel(h.hour)}: ${h.count}`}
                      style={{
                        width: '100%',
                        height: `${Math.round((h.count / peak) * 100)}%`,
                        minHeight: 2,
                        borderRadius: 3,
                        background: h.count ? 'var(--accent)' : 'var(--surface2)',
                      }}
                    />
                    <span style={{ font: '500 9.5px Manrope,sans-serif', color: 'var(--sub)' }}>{hourLabel(h.hour)}</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="press-row"
        onClick={onOpenCrew}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minHeight: 52,
          textAlign: 'left',
          width: '100%',
        }}
      >
        <span style={{ display: 'flex', paddingLeft: 8 }}>
          {onShift.slice(0, 4).map((c) => (
            <AvatarTile
              key={c.attendanceId}
              name={c.name}
              size={28}
              round
              photoUrl={employeeAvatarUrl(c.employeeId)}
              style={{ marginLeft: -8, border: '2px solid var(--surface)' }}
            />
          ))}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', font: '700 13px Manrope,sans-serif' }}>
            {crew.data ? onShift.length : '—'} {t.onShiftNowT}
          </span>
          <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>
            {(crew.data ?? []).length - onShift.length} {t.finishedCountT} · {t.fromAttendanceNoteT} · {t.tapForShiftTimesT}
          </span>
        </span>
        <span style={{ color: 'var(--sub)' }}>›</span>
      </button>
    </>
  );
}

/** "6a", "12p", "2p" — the mock's axis labels. */
export function hourLabel(hour: number): string {
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}
